import React, { useEffect, useState } from 'react';
import { Card, Button, Typography, Modal, Form, Input, Select, message, Descriptions, Tag } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/stores/auth';
import { getUserDetail, updateUser, getRoleList } from '@/api';
import type { Role } from '@/DTO/role';

const { Title } = Typography;
const { Option } = Select;

const Profile: React.FC = () => {
  const { user, login } = useAuthStore();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [updateLoading, setUpdateLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // 获取所有角色列表
  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await getRoleList({ offset: 0, limit: 1000 }); // 获取所有角色
      if (response.code === 0) {
        setRoles(response.data.data);
      }
    } catch (error) {
      console.error('获取角色列表失败:', error);
    } finally {
      setRolesLoading(false);
    }
  };

  // 组件加载时获取角色列表
  useEffect(() => {
    fetchRoles();
  }, []);

  // 根据角色ID匹配角色名称
  const getRoleNamesByIds = (roleIds: number[]) => {
    return roles.filter(role => roleIds.includes(role.id));
  };

  // 渲染用户角色标签
  const renderUserRoles = () => {
    if (!user?.roles || user.roles.length === 0) return '-';
    
    return user.roles.map((role) => (
      <Tag key={role.id}  style={{ marginBottom: '4px' }}>
        {role.role_name}
      </Tag>
    ));
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!user) return;
      setUpdateLoading(true);
      const values = await form.validateFields();
      console.log('values', values);
      values.id = user.id; // 确保传递用户ID
      const response = await updateUser(values);
      console.log('response', response);
      const userDetails = await getUserDetail({ id: user.id }); // 刷新用户详情
      console.log('userDetails', userDetails);
      login(userDetails.data);

      message.success('个人信息更新成功');
      setIsEditModalOpen(false);
      form.resetFields();
    } catch (error) {
      console.error('表单验证失败:', error);
    } finally {
      setUpdateLoading(false);
    }
  };
  if (!user) return null;
  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>我的资料</Title>

      <Card
        extra={
          <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
            编辑
          </Button>
        }
      >
        <Descriptions column={1} labelStyle={{ width: '120px' }}>
          <Descriptions.Item label="邮箱">{user.email}</Descriptions.Item>
          <Descriptions.Item label="姓名">{user.username}</Descriptions.Item>
          <Descriptions.Item label="所属组织机构">{user.organization.organization_name}</Descriptions.Item>
          <Descriptions.Item label="联系电话">{user.phone}</Descriptions.Item>
          <Descriptions.Item label="角色">
            {renderUserRoles()}
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color="green">{user.status === 0 ? '激活' : '禁用'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="备注">
            <div style={{ maxWidth: '600px', wordWrap: 'break-word' }}>{user.describe}</div>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 编辑弹窗 */}
      <Modal title="编辑" open={isEditModalOpen} onOk={handleSubmit} onCancel={() => setIsEditModalOpen(false)} destroyOnHidden okText="保存" cancelText="取消" confirmLoading={updateLoading}>
        <Form form={form} layout="vertical">
          <Form.Item required label="邮箱地址">
            <Input value={user.email} disabled />
          </Form.Item>

          <Form.Item name="username" label="姓名" initialValue={user.username} rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="请输入姓名" />
          </Form.Item>

          <Form.Item name="phone" label="联系电话" initialValue={user.phone} rules={[{ required: false, message: '请输入联系电话' }]}>
            <Input placeholder="请输入联系电话" />
          </Form.Item>

          <Form.Item required label="所属机构">
            <Select value={user.organization.organization_name} disabled>
              <Option value={user.organization.organization_name}>{user.organization.organization_name}</Option>
            </Select>
          </Form.Item>

          <Form.Item required label="角色">
            <Select
              mode="multiple"
              value={user.roles?.map(role => role.id) || []}
              disabled
              loading={rolesLoading}
              placeholder="角色信息"
            >
              {roles.map((role) => (
                <Option key={role.id} value={role.id}>
                  {role.role_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item required label="状态">
            <Select value={user.status} disabled style={{ color: '#999' }}>
              <Option value={0}>激活</Option>
              <Option value={1}>禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;
